import React from "react";
import { useDispatch } from "react-redux";

import FiltersDialog from "components/Filters/FiltersDialog";
import SliderFilter from "components/Filters/SliderFilter";
import Loader from "components/Loader";
import RadioButtonsFilter from "components/Filters/RadioButtonsFilter";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import CheckboxesFilter from "components/Filters/CheckboxesFilter/CheckboxesFilter";
import { createAllValuesItem } from "components/Filters/helper";
import { setPreferredConditionsFilters } from "store/actions/racingOfficialPCPage";
import { useRacehorse360Api } from "hooks/api";
import { useStatePreferredConditionFilters } from "../useStatePreferredConditionFilters";
import { getPreferredConditionsFilters } from "./helper";
import {
  initialDaysSinceLastRace,
  MAX_VALUE,
  MIN_DISTANCE,
  MIN_VALUE,
  SUB_TITLE,
  genderTypes,
  surfaceTypes,
  ageTypes
} from "./filtersOptions";

interface IProps {
  onClose: () => void;
}

const PreferredConditionsFilters = (props: IProps) => {
  const { onClose } = props;
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();
  const {
    daysSinceLastRace,
    setDaysSinceLastRace,
    selectedSurface,
    setSelectedSurface,
    selectedGender,
    setSelectedGender,
    selectedDistances,
    setSelectedDistances,
    selectedRaceTypes,
    setSelectedRaceTypes,
    selectedClaimingPrices,
    setSelectedClaimingPrices,
    selectedAge,
    setSelectedAge,
    hasChangedFilterValue,
    hasChangedSavedFiltersValues
  } = useStatePreferredConditionFilters();

  const { usePullRegionPreferredConditions, useGetFacility } =
    useRacehorse360Api();

  const handleResetFilters = () => {
    setDaysSinceLastRace(initialDaysSinceLastRace);
    setSelectedSurface(surfaceTypes[0].value);
    setSelectedGender(genderTypes[0].value);
    setSelectedDistances(null);
    setSelectedClaimingPrices(null);
    setSelectedAge(null);
    setSelectedRaceTypes(null);
  };

  const handleApplyFilters = () => {
    dispatch(
      setPreferredConditionsFilters({
        daysSinceLastRace,
        selectedSurface,
        selectedGender,
        selectedDistances,
        selectedRaceTypes,
        selectedClaimingPrices,
        selectedAge
      })
    );
    onClose();
  };

  const { data: facilityData, isLoading: isLoadingFacilityData } =
    useGetFacility({
      id: currentUser.racingOfficialFacilityIds[0],
      getOptions: {
        select: ["region"]
      }
    });

  const { data: preferredConditions, isLoading: isLoadingPreferredConditions } =
    usePullRegionPreferredConditions(
      {
        id: {
          value: facilityData?.region?.id || null
        }
      },
      {
        enabled: Boolean(!isLoadingFacilityData && facilityData)
      }
    );

  const { claimingPriceTypes, distanceTypes, raceTypes } =
    getPreferredConditionsFilters(
      preferredConditions?.regionPreferredConditions
    );
  const isLoading = isLoadingFacilityData || isLoadingPreferredConditions;

  return (
    <FiltersDialog
      onClose={onClose}
      onResetFilters={handleResetFilters}
      onApplyFilters={handleApplyFilters}
      title="Preferred Conditions Filter"
      disabledResetButton={!hasChangedFilterValue}
      disabledApplyButton={!hasChangedSavedFiltersValues}
      minHeightDialog={553}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SliderFilter
            maxValue={MAX_VALUE}
            minValue={MIN_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={daysSinceLastRace}
            onChangeSelectedValues={setDaysSinceLastRace}
            filterTitle="Days Since Last Race"
            subTitle={SUB_TITLE}
          />
          <RadioButtonsFilter
            title={"Surface"}
            data={surfaceTypes}
            selectedValue={selectedSurface}
            onChangeSelectedValue={setSelectedSurface}
          />
          <CheckboxesFilter
            title="Distance"
            data={[createAllValuesItem("Distances"), ...distanceTypes]}
            selectedValues={selectedDistances}
            onChangeSelectedValues={setSelectedDistances}
            maxWidthPreview={300}
            maxMobileWidthPreview={60}
            withLimit={true}
          />
          <CheckboxesFilter
            title="Race Type"
            data={[createAllValuesItem("Race Types"), ...raceTypes]}
            selectedValues={selectedRaceTypes}
            onChangeSelectedValues={setSelectedRaceTypes}
            maxWidthPreview={300}
            maxMobileWidthPreview={60}
            withLimit={true}
          />
          <CheckboxesFilter
            title="Claiming Price"
            data={[
              createAllValuesItem("Claiming Prices"),
              ...claimingPriceTypes
            ]}
            selectedValues={selectedClaimingPrices}
            onChangeSelectedValues={setSelectedClaimingPrices}
            maxMobileWidthPreview={60}
            withLimit={true}
          />
          <RadioButtonsFilter
            title={"Gender"}
            data={genderTypes}
            selectedValue={selectedGender}
            onChangeSelectedValue={setSelectedGender}
          />
          <CheckboxesFilter
            title="Age"
            data={[createAllValuesItem("Ages"), ...ageTypes]}
            selectedValues={selectedAge}
            onChangeSelectedValues={setSelectedAge}
            maxMobileWidthPreview={60}
            withLimit={true}
          />
        </>
      )}
    </FiltersDialog>
  );
};

export default PreferredConditionsFilters;
